import { guff } from 'bam-node-baseball-savant/utils/guff'

function threeDigitFormat(val, placeholder = null) {
    try {
        if (val === null || val === undefined) {
            return placeholder
        }

        const num = Number(val)

        if (num >= 0 && num < 1) {
            return num.toFixed(3).slice(-4)
        }
        // Negative numbers? Meh
        return num.toFixed(3)
    } catch (e) {
        console.log('threeDigitFormat error', e, val) // eslint-disable-line no-console

        return val
    }
}

const batTrackingMetricKeys = {
    attackAngle: {
        dbProp: 'attack_angle',
    },
    attackDirection: {
        dbProp: 'attack_direction',
    },
    batSpeed: {
        dbProp: 'sweetspot_speed_mph',
    },
    swingLength: {
        dbProp: 'swing_length',
    },
    swingPathTilt: {
        dbProp: 'swing_path_tilt',
    },
    idealAttackAngle: {
        // This isn't a db property, but I add it manually
        dbProp: 'ideal_angle',
    },
}

const batTrackingMetrics = Object.keys(batTrackingMetricKeys)

function addBatTrackingMetricsRaw(calculations, row) {
    batTrackingMetrics.forEach((prop) => {
        const { dbProp } = batTrackingMetricKeys[prop]

        if (!dbProp) {
            throw new Error('missing database property', prop)
        }
        // eslint-disable-next-line no-param-reassign
        calculations[`${prop}Sum`] += row.trackedBatSpeed
            ? Number(row.raw[dbProp])
            : 0
        // eslint-disable-next-line no-param-reassign
        calculations[`${prop}CompetitiveSum`] += row.trackedBatSpeedCompetitive
            ? Number(row.raw[dbProp])
            : 0
    })
}

function addBatTrackingMetricsPercents(
    calculations,
    trackedBatSpeed,
    trackedBatSpeedCompetitive
) {
    batTrackingMetrics.forEach((prop) => {
        // const { guffType } = batTrackingMetricKeys[prop]
        // eslint-disable-next-line no-param-reassign
        calculations[prop] = trackedBatSpeed
            ? calculations[`${prop}Sum`] / trackedBatSpeed
            : null

        // eslint-disable-next-line no-param-reassign
        calculations[`${prop}Competitive`] = trackedBatSpeedCompetitive
            ? calculations[`${prop}CompetitiveSum`] / trackedBatSpeedCompetitive
            : null
    })
}

export default function runCalculations(data, totalSize) {
    const trackedSwingLength = data.filter((d) => d.trackedSwingLength).length
    const trackedSwingLengthCompetitive = data.filter(
        (d) => d.trackedSwingLengthCompetitive
    ).length
    const trackedBatSpeed = data.filter((d) => d.trackedBatSpeed).length
    const trackedBatSpeedCompetitive = data.filter(
        (d) => d.trackedBatSpeedCompetitive
    ).length

    const calculations = {
        totalPitches: data.length,
        hits: 0,
        bip: 0,
        trackedBIP: 0,
        trackedSwingLength,
        trackedBatSpeed,
        trackedSwingLengthCompetitive,
        trackedBatSpeedCompetitive,
        ab: 0,
        pa: 0,
        hr: 0,
        so: 0,
        single: 0,
        double: 0,
        triple: 0,
        pitches: 0,
        wobaNumer: 0,
        wobaDenom: 0,
        xWobaNumer: 0,
        xWobaDenom: 0,
        runExp: 0,
        pitcherRunExp: 0,
        swings: 0,
        whiffs: 0,
        launchSpeed: 0,
        launchAngle: 0,
        releaseSpeedSum: 0,
        releaseSpinRateSum: 0,
        batSpeedSum: 0,
        batSpeedCompetitiveSum: 0,
        swingLengthSum: 0,
        swingLengthCompetitiveSum: 0,
        idealAttackAngleSum: 0,
        idealAttackAngleCompetitiveSum: 0,
        attackAngleSum: 0,
        attackAngleCompetitiveSum: 0,
        attackDirectionSum: 0,
        attackDirectionCompetitiveSum: 0,
        swingPathTiltSum: 0,
        swingPathTiltCompetitiveSum: 0,
        // Averages
        percent: null,
        avgExitVelocity: null,
        avgLaunchAngle: null,
        ba: null,
        woba: null,
        slg: null,
        slgRaw: null,
        xWoba: null,
        kPercent: null,
        swing_miss_percent: null,
        releaseSpeed: null,
        releaseSpinRate: null,
        batSpeed: null,
        batSpeedCompetitive: null,
        swingLength: null,
        swingLengthCompetitive: null,
        idealAttackAngle: null,
        idealAttackAngleCompetitive: null,
    }
    const trackedSpin = data.filter((d) => d.releaseSpinRate).length
    const trackedSpeed = data.filter((d) => d.releaseSpeed).length

    for (let i = 0; i < data.length; i++) {
        const d = data[i]

        d.raw.ideal_angle =
            d.trackedBatSpeed &&
            d.raw.attack_angle >= 5 &&
            d.raw.attack_angle <= 20
                ? 1
                : 0

        calculations.hits += Number(d.hits)
        calculations.ab += Number(d.ab)
        calculations.pa += Number(d.pa)
        calculations.bip += Number(d.bip)
        calculations.swings += d.is_swing ? d.is_swing : 0
        calculations.whiffs += d.is_whiff ? d.is_whiff : 0
        if (d.launchSpeed > 0) {
            calculations.trackedBIP += 1
        }
        calculations.single += Number(d.single)
        calculations.double += Number(d.double)
        calculations.triple += Number(d.triple)
        calculations.hr += Number(d.hr)
        calculations.so += Number(d.so)
        calculations.launchSpeed += !Number.isNaN(d.launchSpeed)
            ? Number(d.launchSpeed)
            : 0
        calculations.launchAngle += !Number.isNaN(d.launchAngle)
            ? Number(d.launchAngle)
            : 0
        calculations.wobaNumer += Number(d.wobaNumer)
        calculations.wobaDenom += Number(d.wobaDenom)
        calculations.xWobaNumer += Number(d.xWobaNumer)
        calculations.xWobaDenom += Number(d.xWobaDenom)

        // pitch
        calculations.releaseSpeedSum += Number.isNaN(d.releaseSpeed)
            ? 0
            : d.releaseSpeed
        calculations.releaseSpinRateSum += Number.isNaN(d.releaseSpinRate)
            ? 0
            : d.releaseSpinRate

        calculations.runExp += Number.isNaN(d.deltaRunExp) ? 0 : d.deltaRunExp
        calculations.pitcherRunExp -= Number.isNaN(d.deltaRunExp)
            ? 0
            : d.deltaRunExp

        // Add bat tracking metrics
        addBatTrackingMetricsRaw(calculations, d)

        if (
            d.pitchType !== '' &&
            !['automatic_ball', 'automatic_strike', 'intent_ball'].includes(
                d.event_type
            )
        ) {
            calculations.pitches += 1
        }
    }

    // Averages/Percents
    calculations.percent = ((calculations.pitches / totalSize) * 100).toFixed(1)

    calculations.avgExitVelocity = calculations.trackedBIP
        ? (calculations.launchSpeed / calculations.trackedBIP).toFixed(1)
        : null

    calculations.avgLaunchAngle = calculations.trackedBIP
        ? (calculations.launchAngle / calculations.trackedBIP).toFixed(0)
        : null

    calculations.ba = calculations.ab
        ? removeLeadingZero((calculations.hits / calculations.ab).toFixed(3))
        : null

    calculations.woba = calculations.wobaDenom
        ? removeLeadingZero(
              (calculations.wobaNumer / calculations.wobaDenom).toFixed(3)
          )
        : null

    if (calculations.ab) {
        const slg =
            (calculations.single +
                2 * calculations.double +
                3 * calculations.triple +
                4 * calculations.hr) /
            calculations.ab

        calculations.slgRaw = slg
        calculations.slg = threeDigitFormat(slg)
    } else {
        calculations.slg = null
    }

    if (calculations.xWobaDenom) {
        calculations.xWoba = removeLeadingZero(
            (calculations.xWobaNumer / calculations.xWobaDenom).toFixed(3)
        )
    }

    calculations.kPercent = calculations.pa
        ? ((calculations.so / calculations.pa) * 100).toFixed(1)
        : null

    calculations.swing_miss_percent = calculations.swings
        ? guff(
              (100 * calculations.whiffs) / calculations.swings,
              'swing_miss_percent'
          )
        : null

    calculations.releaseSpeed = trackedSpeed
        ? (calculations.releaseSpeedSum / trackedSpeed).toFixed(1)
        : 0

    calculations.releaseSpinRate = trackedSpin
        ? Math.round(calculations.releaseSpinRateSum / trackedSpin)
        : null

    calculations.runExp = calculations.runExp.toFixed(1)
    calculations.pitcherRunExp = calculations.pitcherRunExp.toFixed(1)

    addBatTrackingMetricsPercents(
        calculations,
        trackedBatSpeed,
        trackedBatSpeedCompetitive
    )

    return calculations
}

/* eslint-disable no-param-reassign */
function removeLeadingZero(value) {
    try {
        if (value.substring(0, 1) === '0') {
            value = value.substring(1, value.length)
        } else if (value.substring(0, 2) === '-0') {
            value = `-${value.substring(2, value.length)}`
        }
    } catch (ex) {
        /* empty */
    }

    return value
}
/* eslint-enable no-param-reassign */
